import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSpaces, useBackendStatus } from '../data/firestore';
import { seedSpaces, CATEGORY_LABELS } from '../data/seedSpaces';
import { ROOMS } from '../data/rooms';
import { useAuth } from '../AuthContext';
import AssetImage from '../components/AssetImage';

// Dashboard: 3D house hero, then room preview cards (one per physical room —
// the fastest path into blueprints and design renders), with the full
// space-by-space cards at the bottom. Moodboard collages live on /designs.

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${u[i]}`;
};

const fmtMoney = (n) =>
  n ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '';

const STATUS_META = {
  planning: { label: 'Planning', color: '#9aa0a6' },
  furnishing: { label: 'Furnishing', color: '#8ab4f8' },
  'in-progress': { label: 'In progress', color: '#ffd166' },
  done: { label: 'Done', color: '#7ce38b' },
};

function RoomCard({ room, spaces }) {
  const statuses = room.sections
    .map((s) => (spaces || []).find((sp) => sp.id === s.spaceId))
    .filter(Boolean)
    .map((sp) => STATUS_META[sp.status] || STATUS_META.planning);
  const st = statuses[0];

  return (
    <div className="cs-card ah-room-card">
      <Link to={`/rooms/${room.id}`} className="ah-room-card-img">
        {room.previewS3Key
          ? <AssetImage s3Key={room.previewS3Key} alt={room.name} />
          : <span className="ah-room-card-fallback">{room.icon}</span>}
      </Link>
      <div className="ah-room-card-body">
        <div className="ah-space-head" style={{ marginBottom: 2 }}>
          <span className="ah-space-icon">{room.icon}</span>
          <strong>{room.name}</strong>
          <span style={{ flex: 1 }} />
          {st && <span className="ah-status" style={{ color: st.color }}>{st.label}</span>}
        </div>
        <p className="cs-help" style={{ margin: '2px 0 8px' }}>{room.blurb}</p>
        <div className="ah-room-card-links">
          <Link to={`/rooms/${room.id}?view=render`}>Renders</Link>
          <Link to={`/rooms/${room.id}?view=blueprint`}>Blueprints</Link>
          <Link to={`/rooms/${room.id}?view=plumbing`}>Plumbing</Link>
          <Link to={`/rooms/${room.id}?view=moodboards`}>Moodboards</Link>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { docs: spaces } = useSpaces();
  const { doc: status } = useBackendStatus();
  const [seeding, setSeeding] = useState(false);

  const backendLive = status?.heartbeat && Date.now() - status.heartbeat < 3 * 60 * 1000;
  const totalBudget = (spaces || []).reduce((s, sp) => s + (Number(sp.budget) || 0), 0);
  const totalPhotos = (spaces || []).reduce((s, sp) => s + (sp.assetCount || 0), 0);

  const groups = ['interior', 'outdoor', 'exterior']
    .map((cat) => ({
      cat,
      label: CATEGORY_LABELS[cat],
      list: (spaces || []).filter((sp) => (sp.category || 'interior') === cat),
    }))
    .filter((g) => g.list.length);

  return (
    <div className="ah-home">
      <div className="ah-hero">
        <img src="/property/hero-iso-3d-v4.jpg" alt="Amalfi House — 3D site plan" />
        <div className="ah-hero-scrim" />
        <div className="ah-hero-text">
          <h1>Amalfi House</h1>
          <p>Pacific Palisades · renovation HQ</p>
          <div className="ah-hero-stats">
            <span title="configs/app heartbeat, written by the backend every minute">
              backend {backendLive ? '● online' : '○ offline'}
            </span>
            <span>{(spaces || []).length} spaces</span>
            <span>{totalPhotos} files</span>
            {totalBudget > 0 && <span>{fmtMoney(totalBudget)} budgeted</span>}
          </div>
        </div>
      </div>

      <div className="cs-page" style={{ paddingTop: 24 }}>
        {spaces === null && <p className="cs-help">Loading…</p>}

        {spaces && !spaces.length && (
          <div className="cs-empty" style={{ marginTop: 8 }}>
            {user ? (
              <>
                <p style={{ marginTop: 0 }}>
                  No spaces yet. Seed the renovation plan — 4 bedrooms, 4 bathrooms + powder
                  room, pool, sauna, outdoor showers, yards, exterior and garage.
                </p>
                <button
                  className="cs-btn cs-btn-primary"
                  disabled={seeding}
                  onClick={async () => {
                    setSeeding(true);
                    try {
                      await seedSpaces();
                    } finally {
                      setSeeding(false);
                    }
                  }}
                >
                  {seeding ? 'Seeding…' : 'Create the 16 renovation spaces'}
                </button>
              </>
            ) : (
              'Sign in to set up the renovation plan.'
            )}
          </div>
        )}

        <section style={{ marginTop: 4 }}>
          <div className="ah-collage-head">
            <h2 className="ah-group-title" style={{ margin: 0 }}>Rooms</h2>
            <Link to="/designs" className="cs-help ah-collage-more">all moodboards →</Link>
          </div>
          <div className="ah-room-grid">
            {ROOMS.map((r) => <RoomCard key={r.id} room={r} spaces={spaces} />)}
          </div>
        </section>

        {groups.map((g) => (
          <section key={g.cat} style={{ marginTop: 26 }}>
            <h2 className="ah-group-title">{g.label}</h2>
            <div className="ah-grid">
              {g.list.map((sp) => {
                const st = STATUS_META[sp.status] || STATUS_META.planning;
                return (
                  <Link key={sp.id} to={`/spaces/${sp.id}`} className="cs-card ah-space-card">
                    <div className="ah-space-head">
                      <span className="ah-space-icon">{sp.icon || '📐'}</span>
                      <strong>{sp.name}</strong>
                      <span style={{ flex: 1 }} />
                      <span className="ah-status" style={{ color: st.color }}>{st.label}</span>
                    </div>
                    {sp.description && <p className="cs-help">{sp.description}</p>}
                    <div className="ah-space-meta">
                      <span>{sp.assetCount || 0} file{(sp.assetCount || 0) === 1 ? '' : 's'}</span>
                      {sp.totalBytes > 0 && <span>{fmtBytes(sp.totalBytes)}</span>}
                      {Number(sp.budget) > 0 && <span>{fmtMoney(sp.budget)}</span>}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
